import type { PlayerOverlayConfig } from './schema';

/**
 * The player overlay.
 *
 * Every capability is a flag here, and the player components read their flags
 * rather than assuming anything: turning `showHeads` off unmounts the head
 * avatars, it does not hide them with CSS.
 *
 * `source.kind` selects where positions come from:
 * - `bluemap-live`, the live player feed the BlueMap webserver already serves for
 *   online players. This is the only source that works with no extra infrastructure.
 * - `api`, a Minecraft plugin API, via `source.endpoint`. The transport and
 *   normalisation exist (`$lib/api/`); point it at a plugin when one is available.
 * - `none`, the overlay is off no matter what the flags above say.
 *
 * Nothing here fabricates player data. If the configured source is unreachable the
 * overlay reports "unavailable" and the rest of the map keeps working.
 */
export const players: PlayerOverlayConfig = {
	enabled: true,

	showNames: true,
	showHeads: true,
	/** Off by default: exact player positions are more detail than most visitors need. */
	showCoordinates: false,

	searchable: true,
	trackable: true,

	pauseWhenHidden: true,

	source: {
		kind: 'bluemap-live',
		pollIntervalMs: 5_000
	}
};

/**
 * A second, narrower overlay for servers whose BlueMap data has no live feed.
 *
 * Kept as a named alternative rather than a comment so switching a deployment to
 * an API-backed overlay is a one-line import change in `config/index.ts`.
 */
export const playersFromApi: PlayerOverlayConfig = {
	...players,
	source: {
		kind: 'api',
		endpoint: '/players?map={mapId}&world={worldId}',
		pollIntervalMs: 3_000
	}
};
